import React, { useEffect, useRef, useState } from "react";
import { Alert, Modal, Button, Form, Input, notification } from "antd";
import { COMPRESS_IMAGE, POST, PUT } from "../../utils/apiCalls";
import API from "../../config/API";

import ImagePicker from "../components/ImagePicker";
import { useSelector } from "react-redux";
import TextArea from "antd/es/input/TextArea";
import Cropper, { ReactCropperElement } from "react-cropper";
import moment from "moment";
import "cropperjs/dist/cropper.css";
import "./style.scss";

const update = "update";
const BlogUpdateModal = (props: any) => {
  const [form] = Form.useForm();
  const [Notifications, contextHolder] = notification.useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const type = props?.type;
  const [image, setImage] = useState<any>({});
  const User = useSelector((state: any) => state.User.user);
  const fileInputRef = useRef(null);

  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const cropperRef = useRef<ReactCropperElement>(null);

  const [alertVisible, setAlertVisible] = useState(true);

  const handleAlertClose = () => {
    setAlertVisible(false);
  };

  useEffect(() => {
    if (type == update) {
      form.setFieldsValue({
        tags: props?.data?.tags,
        title: props?.data?.title,
        image: props?.data?.image,
        content: props?.data?.content,
        categories: props?.data?.categories,
        description: props?.data?.description,
      });
    } else {
      form.resetFields();
    }
  }, [props.data, type]);

  const formSubmitHandler = async (values: any) => {
    setIsLoading(true);
    try {
      let imageUrl = props?.data?.image;
      if (croppedImage) {
        const ImageBlob = await fetch(croppedImage).then((r) => r.blob());
        let name = moment(new Date()).unix();
        let file = new File([ImageBlob], name + "N.jpg");
        imageUrl = await COMPRESS_IMAGE(file);
      }
      const obj = {
        title: values?.title,
        image: imageUrl?.url,
        categories: values?.categories,
        tags: values?.tags,
        date: moment(new Date()).format("YYYY-MM-DD"),
        description: values?.description,
        content: values?.content,
      };
      setIsLoading(false);
      const responseImg: any =
        type === update
          ? await PUT(API.UPDATE_BLOG + props?.data?.id, obj)
          : await POST(API.ADD_BLOG, obj);
      if (responseImg.status) {
        Notifications["success"]({
          message: "Success",
          description: `Successfully ${
            type === update ? "updated" : "Added"
          } the item`,
        });
        props?.modalClose();
        form.resetFields();
        props?.getBlogsData(props?.page);
        setImage({});
        setCroppedImage(null);
      } else {
        Notifications["error"]({
          message: `Failed to ${type === update ? "Update" : "Add New Item"}`,
          description: responseImg.message ?? "",
        });
      }
    } catch (err: any) {
      Notifications["error"]({
        message: `Failed to ${type === update ? "Update" : "Add New Item"}`,
        description: err.message,
      });
    }
    setIsLoading(false);
  };

  const handleCrop = async () => {
    if (cropperRef.current) {
      const canvas: HTMLCanvasElement | null =
        cropperRef.current.cropper.getCroppedCanvas({
          fillColor: "#fff",
        });
      if (canvas) {
        const croppedData = canvas.toDataURL("image/jpeg");
        setCroppedImage(croppedData);
        setCropModalOpen(false);
      }
    }
  };

  return (
    <Modal
      title={`${type === update ? "Update" : "Add New"} Banner`}
      open={props?.open}
      onOk={form.submit}
      onCancel={() => {
        props?.modalClose();
        form.resetFields();
        setImage({});
      }}
      okText="Update"
      centered
      cancelButtonProps={{ style: { display: "none" } }}
      okButtonProps={{ style: { display: "none" } }}
    >
      {contextHolder}

      {alertVisible && (
        <Alert
          className="small"
          message="Follow below guide before uploading image"
          description={
            <ul>
              <li>
                Use images with light background to highlight the text
                description and title.
              </li>
              <li>
                Banner images are cropped to 4:1 ratio for better visibility.
              </li>
              <li>
                Preferred image formats are JPEG, JPG, or PNG and avoid file
                sizes bigger than 5MB.
              </li>
            </ul>
          }
          type="warning"
          closable
          onClose={handleAlertClose}
        />
      )}
      <Form
        form={form}
        style={{ maxWidth: 600 }}
        layout="vertical"
        onFinish={formSubmitHandler}
      >
        {cropModalOpen ? (
          <div className="mt-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => {
                const selectedFile = e.target.files?.[0];
                if (selectedFile) {
                  setImage({
                    file: selectedFile,
                    url: URL.createObjectURL(selectedFile),
                  });
                  setCropModalOpen(true);
                }
              }}
            />
            <Cropper
              ref={cropperRef as React.RefObject<ReactCropperElement>}
              src={image?.url}
              style={{ height: 400, width: "100%" }}
              guides={true}
            />
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <Button
                style={{ marginRight: 16 }}
                onClick={() => {
                  if (fileInputRef.current) {
                    (fileInputRef.current as any).click();
                  }
                }}
              >
                Choose Another Image
              </Button>
              <Button type="primary" onClick={handleCrop}>
                Crop Image
              </Button>
            </div>
          </div>
        ) : (
          <Form.Item
            name={"image"}
            label="Blog Image"
            rules={[
              {
                required: true,
                message: "Please Select Image",
              },
            ]}
          >
            <ImagePicker
              size="blogType"
              onChange={(file: any) => {
                setImage(file);
                setCropModalOpen(true);
              }}
              fileURL={
                image.file || croppedImage
                  ? croppedImage || image.url
                  : type === update
                  ? props?.data?.image
                  : null
              }
            />
          </Form.Item>
        )}
        <Form.Item
          label="Title"
          name="title"
          className="small"
          rules={[
            {
              required: true,
              message: "Please Enter Title",
            },
          ]}
        >
          <Input placeholder="Title" />
        </Form.Item>
        <Form.Item
          label="Content"
          name="content"
          className="small"
          rules={[
            {
              required: true,
              message: "Please Enter Content",
            },
          ]}
        >
          <TextArea rows={3} placeholder="Content" />
        </Form.Item>
        <Form.Item
          label="Description"
          name="description"
          rules={[
            {
              required: true,
              message: "Please Enter Description",
            },
          ]}
        >
          <TextArea rows={2} placeholder="Description" />
        </Form.Item>
        <Form.Item
          label="Categories"
          name="categories"
          className="small"
          rules={[
            {
              required: true,
              message: "Please Enter Categories",
            },
          ]}
        >
          <Input placeholder="Categories" />
        </Form.Item>
        <Form.Item
          label="Tags"
          name="tags"
          className="small"
          rules={[
            {
              required: true,
              message: "Please Enter Tags",
            },
          ]}
        >
          <Input placeholder="Tags" />
        </Form.Item>

        <div className="d-flex gap-2 justify-content-end">
          <Button
            onClick={() => {
              props?.modalClose();
              form.resetFields();
              setImage({});
            }}
          >
            Cancel
          </Button>
          <Button type="primary" loading={isLoading} onClick={form.submit}>
            {type === update ? "Update" : "Add"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default BlogUpdateModal;
